import { useIntl } from 'react-intl';
import { set, isPlainObject } from 'lodash-es';
import { useStaticQuery, graphql } from 'gatsby';
// import { useLocation } from '@reach/router';

// Locals
import { /* itemIsActive, */ pathUnslash } from '../lib/url';

type YamlItem = Pick<Queries.NavItemsYaml, 'hasDivider' | 'pages' | 'title'>;
type YamlItemPage = NonNullable<YamlItem['pages']>[number];

interface TranslatedString {
    en: string;
    es: string;
    fa: string;
    ru: string;
}

export interface CleanLeaf {
    path: string;
    title: string;
}
export interface CleanSubSection {
    label: TranslatedString;
    pages: CleanLeaf[];
}
export interface CleanCategory {
    title: string;
    pages: Array<CleanLeaf | CleanSubSection>;
    hasDivider: boolean;
}

type LangKey = string; // eg: es
type PageSlug = string; // eg: farm-proxy/installation-and-configuration
type PageUrl = string; // eg: /es/farm-proxy/installation-and-configuration

type FrontMatter = {
    title: string;
    menuLabel?: string;
};
type LookupItem = {
    url: PageUrl;
    frontMatter: FrontMatter;
};
type MapPerPath = Record<PageSlug, LookupItem>;
type MapPerLang = Record<LangKey, MapPerPath>;

function leafPageMapper(
    page: Maybe<{ path: Maybe<string> }>,
    locale: string,
    langMap: MapPerLang,
): undefined | CleanLeaf {
    if (!page?.path) return;

    const slug = pathUnslash(page.path);
    const lngMap = langMap[locale] as Maybe<MapPerPath>;

    const it = lngMap?.[slug];
    const label: string = it?.frontMatter.menuLabel || it?.frontMatter.title || page.path;

    if (!label || !it?.url) return;
    return {
        title: label,
        path: it.url,
    };
}

function leafPageFilter(
    pages: Array<Maybe<{ path: Maybe<string> }>>,
    locale: string,
    langMap: MapPerLang,
): CleanLeaf[] {
    const res: CleanLeaf[] = [];
    pages.forEach(page => {
        const cleanLeaf = leafPageMapper(page, locale, langMap);
        if (cleanLeaf) res.push(cleanLeaf);
    });
    return res;
}

export function useNavItems(): CleanCategory[] {
    const pagesData = useStaticQuery<Queries.MainMenuDataQuery>(graphql`
        query MainMenuData {
            allNavItemsYaml {
                edges {
                    node {
                        title
                        pages {
                            path
                            label {
                                en
                                es
                                fa
                                ru
                            }
                            pages {
                                path
                            }
                        }
                        hasDivider
                    }
                }
            }
            allMdx {
                edges {
                    node {
                        frontmatter {
                            title
                            menuLabel
                        }
                        fields {
                            slug
                            lang
                            path
                        }
                    }
                }
            }
        }
    `);
    const { locale } = useIntl();

    // Reformat the data about pages into a lookup map
    const langMap: MapPerLang = {};
    pagesData.allMdx.edges.forEach(({ node }) => {
        const { fields, frontmatter } = node;

        const slug = fields?.slug;
        const lang = fields?.lang;
        const path = fields?.path;
        if (slug == null || lang == null || path == null) return;

        set(langMap, [lang, slug], { url: path, frontMatter: frontmatter as FrontMatter });
    });

    // Collect the nav items according to our logic around:
    //  - their availability in the current language
    //  - category possibly being hidden & not active
    const result: CleanCategory[] = [];

    pagesData.allNavItemsYaml.edges.map(({ node: navItem }) => {
        // Abort quickly when we get an invalid item
        if (!navItem.title || !Array.isArray(navItem.pages) || !navItem.pages.length) {
            console.error('Invalid nav item, title and pages are required parameters', navItem);
            return;
        }

        const pages: CleanCategory['pages'] = [];

        // Filter to only those items,
        // that have a page with menuLabel/title
        navItem.pages.forEach((page: YamlItemPage) => {
            /**
             * Leaf entry is defined by having
             * following structure:
             *
             * {
             *     path: string;
             * }
             */
            if (page?.path) {
                const cleanLeaf = leafPageMapper(page, locale, langMap);
                if (cleanLeaf) pages.push(cleanLeaf);
            }

            /**
             * Sub-category entry is defined by having a following structure:
             *
             * {
             *     label: Record<Lng, string>;
             *     pages: Array<{ path: string; title: string }>;
             * }
             */
            if (
                // Label is a map of strings for each language
                page?.label &&
                isPlainObject(page.label) &&
                // …and the "pages" are a non-empty array
                Array.isArray(page.pages) &&
                pages.length > 0
            ) {
                pages.push({
                    label: page.label as TranslatedString,
                    // Child pages here can only be leaf pages, so we can safely filter & cast
                    pages: leafPageFilter(page.pages, locale, langMap),
                });
            }
        });

        // If there are no pages, skip this nav item
        if (!pages.length) return;

        // // Skip the nav item if it is marked as "hidden"
        // if (navItem.schrodinger === 'cat') {
        //     const hasActivePage: boolean = pages.some(page => itemIsActive(page.path, pathname, 2));
        //     // …and it doesn't have an active item
        //     if (!hasActivePage) return;
        // }

        result.push({
            pages,
            title: navItem.title,
            hasDivider: !!navItem.hasDivider,
        });
    });

    return result;
}
